import React, { useRef } from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import { UpdateWarehouseRequest, WarehouseModel } from "../../api/warehouse.api"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { deleteWarehouse, updateWarehouse } from "../../store/warehouse.store"

function WarehousesTable({
    warehouses,
    warehouseDetails,
    setWarehouseDetails,
    setShow
}: {
    warehouses: WarehouseModel[]
    warehouseDetails: Record<string, any>
    setWarehouseDetails: Function
    setShow: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const exportBtn = useRef(null)

    const warehousesColumns = [
        {
            dataField: "id",
            text: t("#")
        },
        {
            text: t("Warehouse_name"),
            dataField: "name"
        },
        {
            text: t("Warehouse_address"),
            dataField: "address"
        },
        {
            text: t("Warehouse_zipCode"),
            dataField: "zip_code"
        },
        {
            text: t("No. of Users"),
            dataField: "ParcelHubUserWarehouse",
            sort: true,
            formatter: (cell: Record<string, any>[], row: Record<string, any>[]) => {
                return cell?.length
            },
            csvFormatter: (cell: Record<string, any>[], row: Record<string, any>[]) => {
                return cell ? cell?.length : "-"
            }
        },
        {
            text: t("Country"),
            dataField: "country",
            formatter: (cell: string) => {
                return (
                    <div className="w-25 pt-1">
                        <Flag code={cell} />
                    </div>
                )
            }
        },
        {
            text: t("Phone"),
            dataField: "phone"
        },
        {
            text: t("Status"),
            dataField: "active",
            formatter: (cell: number | boolean) => {
                return cell === 1 || cell === true ? (
                    <span className="status">{t("Active")}</span>
                ) : (
                    <span className="expired"> {t("Inactive")} </span>
                )
            },
            csvFormatter: (cell: number, row: Record<string, any>[]) => {
                return cell === 1 ? t("Active") : t("Inactive")
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Edit", "Activate/Deactivate"].map((action) => (
                                <Dropdown.Item
                                    key={
                                        action !== "Activate/Deactivate"
                                            ? action
                                            : row.active
                                              ? "Deactivate"
                                              : "Activate"
                                    }
                                    className="py-0"
                                    onClick={() => {
                                        if (action !== "Activate/Deactivate") {
                                            setWarehouseDetails({ ...row })
                                            setShow(true)
                                        } else {
                                            dispatch(
                                                updateWarehouse({
                                                    ...row,
                                                    active: !row.active
                                                } as UpdateWarehouseRequest)
                                            )
                                        }
                                    }}>
                                    {action !== "Activate/Deactivate"
                                        ? t(action)
                                        : row.active
                                          ? t("Deactivate")
                                          : t("Activate")}
                                </Dropdown.Item>
                            ))}
                            {row?.ParcelHubUserWarehouse?.length === 0 && (
                                <Dropdown.Item
                                    className="py-0"
                                    key={"Delete"}
                                    onClick={() => dispatch(deleteWarehouse(row?.id))}>
                                    {t("Delete")}
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        }
    ]

    const expandRow = {
        renderer: (row: any): JSX.Element => {
            return (
                <Row className="expanding-row px-4 py-4">
                    <Col xs={12} sm={12} md={12} lg={5}>
                        <div className="heading">{t("Users")}</div>
                        {/* <Row className="expand-data">
							<Col xs={12} sm={12} md={12} lg={4} className="label">{t("First_Name")}</Col>
							<Col xs={12} sm={12} md={12} lg={8} className="value">{row.fname}</Col>
						</Row> */}
                    </Col>
                </Row>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {warehousesColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={warehouses}
                    columns={warehousesColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "WarehousesTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search warehouses-search">
                                <Col xs={12} sm={12} md={12} lg={12} className="d-flex justify-content-end">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                {/* <Col className="d-none"> */}
                                {/* <ExportCSVButton {...props.csvProps}>
											Export
										</ExportCSVButton> */}
                                {/* </Col> */}
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        expandRow={expandRow}
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{ dataField: "email_verified_at", order: "desc" }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default WarehousesTable
